// Diagnostic admin view of an extraction run. Pulls heavy fields
// (extractionMeta, diResult) off the project DTO and breaks them into
// collapsible sections. Each section corresponds to one step of the
// pipeline so debugging "why did this drawing produce X?" doesn't need
// a DB round-trip.
//
// Gated on isAdmin in ProjectDetail — non-admins don't see the tab.
import type { TakeoffProjectDto } from "../../../../../shared/types/pipe-takeoff";
import { AdminSection, JsonBlock } from "./AdminSection";
import { AdminDiTables } from "./AdminDiTables";

interface Props {
  project: TakeoffProjectDto;
}

interface ExtractionMeta {
  modelMeta?: Array<{
    pass: string;
    modelId: string;
    inputTokens: number;
    outputTokens: number;
    estimatedCostUsd: number;
    latencyMs: number;
  }>;
  flags?: Array<{ rule: string; severity: string; message: string; itemIndex?: number }>;
  scanInventory?: Record<string, unknown>;
  bomTables?: number;
  bomItems?: unknown[];
  referenceImagesUsed?: number;
  referenceImageTypes?: string[];
  referenceImageList?: Array<{
    filename: string;
    typeName: string;
    typeDescription: string | null;
    caption: string | null;
  }>;
}

interface DiResult {
  // The pipeline persists DI's raw analyseResult, so the markdown lives
  // at .content (DI's API field name). Older saved runs may have used
  // the processed shape with .rawMarkdown — fall back to that.
  content?: string;
  rawMarkdown?: string;
  tables?: Array<{
    rowCount: number;
    columnCount: number;
    headers?: string[];
    cells?: Array<{ rowIndex: number; columnIndex: number; content: string }>;
  }>;
  pages?: Array<{ pageNumber: number; width?: number; height?: number; unit?: string }>;
}

export function AdminTab({ project }: Props) {
  const meta = (project.extractionMeta ?? {}) as ExtractionMeta;
  const di = (project.diResult ?? {}) as DiResult;

  if (project.status !== "completed" && project.status !== "failed") {
    return (
      <p style={{ color: "var(--bsp-fg-muted)" }}>
        Extraction has not completed yet — diagnostics will appear here once it has.
      </p>
    );
  }

  const totalCost = (meta.modelMeta ?? []).reduce((s, m) => s + (m.estimatedCostUsd ?? 0), 0);
  const totalTokens = (meta.modelMeta ?? []).reduce(
    (s, m) => s + (m.inputTokens ?? 0) + (m.outputTokens ?? 0),
    0,
  );

  return (
    <div>
      <div className="bsp-card" style={{ marginBottom: 12, fontSize: 13 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Stat label="Status" value={project.status} />
          <Stat label="DI tables (BOM-shaped)" value={meta.bomTables ?? 0} />
          <Stat label="Reference images" value={meta.referenceImagesUsed ?? 0} />
          <Stat label="Validation flags" value={meta.flags?.length ?? 0} />
          <Stat label="Total cost" value={`$${totalCost.toFixed(4)}`} />
          <Stat label="Total tokens" value={totalTokens.toLocaleString()} />
        </div>
      </div>

      <AdminSection title="Pass 1 — Scan inventory" badge={Object.keys(meta.scanInventory ?? {}).length || null}>
        <JsonBlock value={meta.scanInventory} />
      </AdminSection>

      <AdminSection title="DI tables (raw)" badge={di.tables?.length ?? 0}>
        <AdminDiTables tables={di.tables ?? []} />
      </AdminSection>

      {(() => {
        const md = di.content ?? di.rawMarkdown ?? "";
        return (
          <AdminSection title="DI markdown (raw)" badge={md.length}>
            <pre style={{
              margin: 0, padding: 10,
              background: "var(--ink-100, #F5F7FA)",
              border: "1px solid var(--bsp-border)",
              borderRadius: 4,
              fontSize: 11, lineHeight: 1.45,
              overflow: "auto", maxHeight: 480,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>
              {md || "(none)"}
            </pre>
          </AdminSection>
        );
      })()}

      <AdminSection title="Deterministic BOM items (pre-merge)" badge={meta.bomItems?.length ?? 0}>
        <JsonBlock value={meta.bomItems} />
      </AdminSection>

      <AdminSection title="Reference images sent" badge={meta.referenceImagesUsed ?? 0}>
        <JsonBlock value={meta.referenceImageList ?? meta.referenceImageTypes} />
      </AdminSection>

      <AdminSection title="Validation flags" badge={meta.flags?.length ?? 0}>
        <JsonBlock value={meta.flags} />
      </AdminSection>

      <AdminSection title="Model meta (tokens + cost + latency)" badge={meta.modelMeta?.length ?? 0}>
        <JsonBlock value={meta.modelMeta} />
      </AdminSection>

      <AdminSection title="Full extraction_meta (raw JSON)">
        <JsonBlock value={meta} />
      </AdminSection>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="bsp-label" style={{ marginBottom: 2 }}>{label}</div>
      <div className="bsp-mono" style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
}
